import type { RedirectRequest } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { Box, Button, TextField } from '@mui/material';
import { useLocation } from 'react-router-dom';

type Props = {
  loginProps: RedirectRequest;
  baseUrl: string;
  /** @default 'email' */
  emailLabel?: string;
  /** @default 'enter email' */
  emailPlaceholder?: string;
  /** @default 'Continue' */
  continueLabel?: string;
};

const Login = ({
  loginProps,
  baseUrl,
  emailLabel = 'email',
  emailPlaceholder = 'enter email',
  continueLabel = 'Continue',
}: Props): JSX.Element => {
  const { instance } = useMsal();
  const { search } = useLocation();

  const searchParams = new URLSearchParams(search);
  const redirectPath = searchParams.get('redirectPath');

  const handleLoginRedirect = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const form = new FormData(event.currentTarget);
    const email = String(form.get('email') ?? '');
    if (!email) {
      return;
    }

    const domainHint = email.split('@')?.[1] ?? '';
    const redirectStartUrl = new URL(baseUrl, window.location.origin);
    if (redirectPath) {
      redirectStartUrl.searchParams.set('redirectPath', redirectPath);
    }
    const redirectStartPage = `${redirectStartUrl.pathname}${redirectStartUrl.search}`;

    instance
      .loginRedirect({
        ...loginProps,
        loginHint: email,
        domainHint,
        redirectStartPage,
        state: redirectStartPage,
      })
      .catch((e) => {
        console.error('Login error - loginRedirect', e);
      });
  };

  return (
    <Box
      sx={{
        display: 'grid',
        justifyContent: 'center',
        alignContent: 'center',
        height: '600px',
      }}
    >
      <form onSubmit={handleLoginRedirect}>
        <TextField
          data-heap-redact-text
          label={emailLabel}
          id="email"
          variant="filled"
          size="small"
          type="text"
          name="email"
          placeholder={emailPlaceholder}
        />
        <Button type="submit" variant="contained" color="primary">
          {continueLabel}
        </Button>
      </form>
    </Box>
  );
};

export default Login;
